import React, { useState } from "react";
import { Input, Form, Upload, Button, message } from "antd";
import { UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import { translations } from "../../../utils/Translation/staticTranslation";
import {
  serializeDescriptionForEmail,
  CAMPAIGN_TEMPLATE_DEFAULT,
  CAMPAIGN_TEMPLATE_BILINGUAL_SPLIT,
} from "../../../utils/campaignEmailContent";
import { uploadCampaignFileToS3 } from "../../../utils/campaignsS3Upload";
import RichTextEditor from "./RichTextEditor";

const TemplateImageField = ({ label, hint, value, onChange, viewMode, t }) => {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const { url } = await uploadCampaignFileToS3(file);
      onChange(url);
    } catch (error) {
      message.error(error?.message || "Failed to upload image");
    } finally {
      setUploading(false);
    }
    return false; // prevent antd auto-upload
  };

  return (
    <Form.Item label={label} extra={hint}>
      {value ? (
        <div className="content-image-preview">
          <img src={value} alt={label} className="content-preview-img" />
          {!viewMode && (
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              className="content-remove-img"
              onClick={() => onChange(null)}
            >
              {t?.content?.remove_image || "Remove"}
            </Button>
          )}
        </div>
      ) : viewMode ? (
        <span className="template-image-empty">
          {t?.content?.no_image || "No image"}
        </span>
      ) : (
        <Upload
          accept="image/*"
          showUploadList={false}
          beforeUpload={handleUpload}
          disabled={uploading}
        >
          <Button
            icon={<UploadOutlined />}
            loading={uploading}
            className="content-upload-btn"
          >
            {t?.content?.upload_image || "Upload Image"}
          </Button>
        </Upload>
      )}
    </Form.Item>
  );
};

const ContentStep = ({ formData, setFormData, viewMode = false }) => {
  const lang = localStorage.getItem("language") || "ar";
  const t = translations[lang]?.campaignsPage;

  const handleChange = (field, value) => {
    if (viewMode) return;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const template = formData.template || CAMPAIGN_TEMPLATE_DEFAULT;
  const isBilingualSplit = template === CAMPAIGN_TEMPLATE_BILINGUAL_SPLIT;

  const templateOptions = [
    {
      value: CAMPAIGN_TEMPLATE_DEFAULT,
      title: t?.content?.template_default || "Standard",
      description:
        t?.content?.template_default_desc ||
        "English and Arabic stacked one after the other",
    },
    {
      value: CAMPAIGN_TEMPLATE_BILINGUAL_SPLIT,
      title: t?.content?.template_bilingual_split || "Side by Side",
      description:
        t?.content?.template_bilingual_split_desc ||
        "Optional header image, English and Arabic side by side, optional footer image",
    },
  ];

  return (
    <div className="content-step-container">
      <Form layout="vertical">
        {/* Template selection */}
        <div className="content-field-group-full">
          <Form.Item label={t?.content?.template || "Email Template"}>
            <div className="campaign-template-options">
              {templateOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`campaign-template-card${
                    template === option.value ? " campaign-template-card-active" : ""
                  }`}
                  onClick={() => handleChange("template", option.value)}
                  disabled={viewMode}
                >
                  <div
                    className={`campaign-template-thumb campaign-template-thumb-${option.value}`}
                    aria-hidden="true"
                  >
                    {option.value === CAMPAIGN_TEMPLATE_BILINGUAL_SPLIT ? (
                      <>
                        <span className="thumb-header" />
                        <span className="thumb-columns">
                          <span className="thumb-col" />
                          <span className="thumb-col" />
                        </span>
                        <span className="thumb-footer" />
                      </>
                    ) : (
                      <>
                        <span className="thumb-row" />
                        <span className="thumb-row" />
                      </>
                    )}
                  </div>
                  <div className="campaign-template-card-title">{option.title}</div>
                  <div className="campaign-template-card-desc">
                    {option.description}
                  </div>
                </button>
              ))}
            </div>
          </Form.Item>
        </div>

        {isBilingualSplit && (
          <div className="content-fields-row">
            <TemplateImageField
              label={t?.content?.header_image || "Header Image (Optional)"}
              hint={t?.content?.header_image_hint || "Recommended width 600px"}
              value={formData.headerImage}
              onChange={(url) => handleChange("headerImage", url)}
              viewMode={viewMode}
              t={t}
            />
            <TemplateImageField
              label={t?.content?.footer_image || "Footer Image (Optional)"}
              hint={t?.content?.footer_image_hint || "Recommended width 600px"}
              value={formData.footerImage}
              onChange={(url) => handleChange("footerImage", url)}
              viewMode={viewMode}
              t={t}
            />
          </div>
        )}

        {/* Subject Fields - In One Row */}
        <div className="content-fields-row">
          <Form.Item
            label={t?.content?.subject_english || "Subject (English)"}
          >
            <Input
              placeholder={
                t?.content?.subject_english_placeholder ||
                "Enter subject in English"
              }
              value={formData.subjectEnglish || ""}
              onChange={(e) => handleChange("subjectEnglish", e.target.value)}
              className="content-input"
              disabled={viewMode}
              readOnly={viewMode}
            />
          </Form.Item>

          <Form.Item
            label={t?.content?.subject_arabic || "Subject (Arabic)"}
            required
          >
            <Input
              placeholder={
                t?.content?.subject_arabic_placeholder ||
                "أدخل العنوان بالعربية"
              }
              value={formData.subjectArabic || ""}
              onChange={(e) => handleChange("subjectArabic", e.target.value)}
              className="content-input"
              dir="rtl"
              disabled={viewMode}
              readOnly={viewMode}
            />
          </Form.Item>
        </div>

        {/* Description (English) - Full Width */}
        <div className="content-field-group-full">
          <Form.Item
            label={t?.content?.description_english || "Description (English)"}
          >
            {viewMode ? (
              <div
                className="content-editor-view"
                dangerouslySetInnerHTML={{
                  __html: serializeDescriptionForEmail(formData.descriptionEnglish || ""),
                }}
              />
            ) : (
              <RichTextEditor
                value={formData.descriptionEnglish || ""}
                onChange={(value) => handleChange("descriptionEnglish", value)}
                placeholder={
                  t?.content?.description_english_placeholder ||
                  "Describe your campaign..."
                }
              />
            )}
          </Form.Item>
        </div>

        {/* Description (Arabic) - Full Width */}
        <div className="content-field-group-full">
          <Form.Item
            label={t?.content?.description_arabic || "Description (Arabic)"}
          >
            {viewMode ? (
              <div
                className="content-editor-view"
                dir="rtl"
                dangerouslySetInnerHTML={{
                  __html: serializeDescriptionForEmail(formData.descriptionArabic || ""),
                }}
              />
            ) : (
              <RichTextEditor
                value={formData.descriptionArabic || ""}
                onChange={(value) => handleChange("descriptionArabic", value)}
                placeholder={
                  t?.content?.description_arabic_placeholder ||
                  "وصف حملتك باللغة العربية"
                }
                dir="rtl"
              />
            )}
          </Form.Item>
        </div>
      </Form>
    </div>
  );
};

export default ContentStep;
